import React from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { CreditCard, Pencil, Plus, Search, Star, Trash2, UserRound } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import LoadingLock from '@/components/shared/LoadingLock';
import DataTable from '@/components/shared/DataTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import PersianDatePickerFa from '@/components/shared/PersianDatePickerFa';
import BankAccountModal from '@/components/employees/BankAccountModal';
import {
  accountingApi,
  type BankOption,
  type EmployeeBankAccountRecord,
  type EmployeePayload,
  type EmployeeRecord,
} from '@/lib/api/accounting';
import type { EmployeeAdvanceBankAccount } from '@/lib/api/advances';
import { getExpenseEntries } from '@/lib/mock/expenses';
import { normalizeFaSearchText } from '@/lib/textNormalize';
import { employeeProfilePath } from '@/pages/project-workspace/projectWorkspaceHelpers';

const EmployeesPage: React.FC = () => {
  const [rows, setRows] = React.useState<EmployeeRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [editSubmitting, setEditSubmitting] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [cardFilter, setCardFilter] = React.useState('');
  const [addOpen, setAddOpen] = React.useState(false);
  const [banksOpen, setBanksOpen] = React.useState(false);
  const [bankFormOpen, setBankFormOpen] = React.useState(false);
  const [bankEditAccount, setBankEditAccount] = React.useState<EmployeeAdvanceBankAccount | null>(null);
  const [bankDeleteTarget, setBankDeleteTarget] = React.useState<EmployeeBankAccountRecord | null>(null);
  const [selectedEmployee, setSelectedEmployee] = React.useState<EmployeeRecord | null>(null);
  const [bankRows, setBankRows] = React.useState<EmployeeBankAccountRecord[]>([]);
  const [bankOptions, setBankOptions] = React.useState<BankOption[]>([]);
  const openingBankFormRef = React.useRef(false);

  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [nationalCode, setNationalCode] = React.useState('');
  const [insuranceCode, setInsuranceCode] = React.useState('');
  const [birthDate, setBirthDate] = React.useState('');
  const [mobile, setMobile] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [status, setStatus] = React.useState<EmployeePayload['status']>('active');
  const [editFirstName, setEditFirstName] = React.useState('');
  const [editLastName, setEditLastName] = React.useState('');
  const [editNationalCode, setEditNationalCode] = React.useState('');
  const [editInsuranceCode, setEditInsuranceCode] = React.useState('');
  const [editBirthDate, setEditBirthDate] = React.useState('');
  const [editMobile, setEditMobile] = React.useState('');
  const [editPhone, setEditPhone] = React.useState('');
  const [editStatus, setEditStatus] = React.useState<EmployeePayload['status']>('active');

  const load = React.useCallback(() => {
    setLoading(true);
    accountingApi
      .employees()
      .then((data) => setRows(data))
      .catch((err) => setError(err instanceof Error ? err.message : 'خطا در دریافت لیست کارمندان'))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const resetAddForm = () => {
    setFirstName('');
    setLastName('');
    setNationalCode('');
    setInsuranceCode('');
    setBirthDate('');
    setMobile('');
    setPhone('');
    setStatus('active');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitAdd(false);
  };

  const submitAdd = async (openBankAfterCreate: boolean) => {
    setError('');
    setMessage('');
    if (!firstName.trim() || !lastName.trim()) {
      setError('نام و نام خانوادگی الزامی است.');
      return;
    }
    const nationalDigits = nationalCode.replace(/\D/g, '').slice(0, 10);
    if (nationalDigits && nationalDigits.length !== 10) {
      setError('کد ملی در صورت ورود باید دقیقاً ۱۰ رقم باشد.');
      return;
    }
    setSubmitting(true);
    try {
      const payload: EmployeePayload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        national_code: nationalDigits || null,
        insurance_code: insuranceCode.trim() || undefined,
        birth_date: birthDate.trim() || undefined,
        mobile: mobile.trim() || undefined,
        phone: phone.trim() || undefined,
        status,
      };
      const created = await accountingApi.createEmployee(payload);
      setMessage('کارمند جدید با موفقیت ثبت شد.');
      resetAddForm();
      setAddOpen(false);
      load();
      if (openBankAfterCreate && created?.id) {
        const createdEmployee: EmployeeRecord = {
          id: created.id,
          first_name: payload.first_name,
          last_name: payload.last_name,
          full_name: `${payload.first_name} ${payload.last_name}`.trim(),
          national_code: payload.national_code || null,
          insurance_code: payload.insurance_code || null,
          birth_date: payload.birth_date || null,
          mobile: payload.mobile || null,
          phone: payload.phone || null,
          status: payload.status || 'active',
        };
        await openBanksModal(createdEmployee);
        openBankAccountForm();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ثبت اطلاعات کارمند ناموفق بود.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    setError('');
    setMessage('');
    if (!editFirstName.trim() || !editLastName.trim()) {
      setError('نام و نام خانوادگی الزامی است.');
      return;
    }
    const nationalDigits = editNationalCode.replace(/\D/g, '').slice(0, 10);
    if (nationalDigits && nationalDigits.length !== 10) {
      setError('کد ملی در صورت ورود باید دقیقاً ۱۰ رقم باشد.');
      return;
    }
    setEditSubmitting(true);
    try {
      const payload: EmployeePayload = {
        first_name: editFirstName.trim(),
        last_name: editLastName.trim(),
        national_code: nationalDigits || null,
        insurance_code: editInsuranceCode.trim() || undefined,
        birth_date: editBirthDate.trim() || undefined,
        mobile: editMobile.trim() || undefined,
        phone: editPhone.trim() || undefined,
        status: editStatus,
      };
      await accountingApi.updateEmployee(editingId, payload);
      setMessage('اطلاعات کارمند با موفقیت ویرایش شد.');
      setEditOpen(false);
      setEditingId(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ویرایش کارمند ناموفق بود.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const resetBankForm = React.useCallback(() => {
    setBankEditAccount(null);
  }, []);

  const loadEmployeeBankAccounts = React.useCallback(
    async (employeeId: number) => {
      const data = await accountingApi.employeeBankAccounts(employeeId);
      setBankRows(data.bank_accounts || []);
      setBankOptions(data.banks || []);
    },
    [],
  );

  const openBanksModal = React.useCallback(
    async (employee: EmployeeRecord) => {
      setSelectedEmployee(employee);
      setBanksOpen(true);
      setBankFormOpen(false);
      setError('');
      setMessage('');
      resetBankForm();
      try {
        await loadEmployeeBankAccounts(employee.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'بارگذاری حساب‌های بانکی ناموفق بود.');
      }
    },
    [loadEmployeeBankAccounts, resetBankForm],
  );

  const openBankAccountForm = React.useCallback((account?: EmployeeBankAccountRecord) => {
    if (account) {
      setBankEditAccount({
        id: account.id,
        employee_id: account.employee_id,
        bank_id: account.bank_id,
        account_holder_name: account.account_holder_name,
        account_number: account.account_number,
        iban: account.iban,
        card_number: account.card_number,
        notes: account.notes,
        is_default: account.is_default,
        is_active: account.is_active,
        bank: account.bank ?? null,
      });
    } else {
      setBankEditAccount(null);
    }
    // اول لیست را ببند، بعد از آزاد شدن قفل Radix فرم حساب را باز کن
    openingBankFormRef.current = true;
    setBanksOpen(false);
    window.setTimeout(() => {
      setBankFormOpen(true);
      openingBankFormRef.current = false;
    }, 100);
  }, []);

  const closeBankAccountForm = React.useCallback(
    (reopenList = true) => {
      setBankFormOpen(false);
      resetBankForm();
      if (reopenList && selectedEmployee) {
        setBanksOpen(true);
      }
    },
    [resetBankForm, selectedEmployee],
  );

  const employeeBankAccountUsageCount = React.useCallback(
    (employeeId: number, accountId: number) => {
      const beneficiaryId = `employee-${employeeId}`;
      const expenseCount = getExpenseEntries().filter(
        (x) =>
          x.beneficiaryId === beneficiaryId &&
          Number(x.destinationBankAccountId || 0) === Number(accountId),
      ).length;
      return expenseCount;
    },
    [],
  );

  const toggleBankAccountActive = React.useCallback(
    async (acc: EmployeeBankAccountRecord) => {
      if (!selectedEmployee) return;
      const nextActive = !acc.is_active;
      setError('');
      try {
        await accountingApi.updateEmployeeBankAccount(selectedEmployee.id, acc.id, {
          bank_id: acc.bank_id,
          account_holder_name: acc.account_holder_name,
          account_number: acc.account_number ?? undefined,
          iban: acc.iban ?? undefined,
          card_number: acc.card_number ?? undefined,
          notes: acc.notes ?? undefined,
          is_default: acc.is_default,
          is_active: nextActive,
        });
        setBankRows((prev) =>
          prev.map((row) => (row.id === acc.id ? { ...row, is_active: nextActive } : row)),
        );
        setMessage(nextActive ? 'حساب بانکی فعال شد.' : 'حساب بانکی غیرفعال شد.');
        load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'تغییر وضعیت حساب ناموفق بود.');
      }
    },
    [selectedEmployee, load],
  );

  const filteredRows = React.useMemo(() => {
    const q = normalizeFaSearchText(searchTerm);
    return rows.filter((row) => {
      if (statusFilter && (row.status || 'inactive') !== statusFilter) return false;
      if (cardFilter === 'inactive' && !row.has_inactive_bank_account) return false;
      if (!q) return true;
      const haystack = [
        row.full_name,
        row.first_name,
        row.last_name,
        row.employee_code,
        row.national_code,
        row.mobile,
      ]
        .filter(Boolean)
        .join(' ');
      return normalizeFaSearchText(haystack).includes(q);
    });
  }, [rows, searchTerm, statusFilter, cardFilter]);

  const columns = React.useMemo<ColumnDef<EmployeeRecord>[]>(
    () => [
      {
        id: 'name',
        header: 'نام و نام خانوادگی',
        cell: ({ row }) => {
          const label =
            row.original.full_name ||
            `${row.original.first_name || ''} ${row.original.last_name || ''}`.trim() ||
            '—';
          return (
            <Link
              to={employeeProfilePath(row.original.id)}
              className="font-medium text-sky-700 hover:text-sky-900 hover:underline"
            >
              {label}
            </Link>
          );
        },
      },
      { accessorKey: 'employee_code', header: 'کد پرسنلی' },
      { accessorKey: 'national_code', header: 'کد ملی' },
      { accessorKey: 'mobile', header: 'موبایل' },
      {
        accessorKey: 'status',
        header: 'وضعیت',
        cell: ({ row }) => {
          const s = row.original.status || 'inactive';
          return s === 'active'
            ? 'فعال'
            : s === 'vacation'
              ? 'مرخصی'
              : s === 'terminated'
                ? 'خاتمه'
                : 'غیرفعال';
        },
      },
      {
        id: 'actions',
        header: 'عملیات',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-violet-600 hover:bg-violet-50"
              title="پروفایل و گردش حساب"
              asChild
            >
              <Link to={employeeProfilePath(row.original.id)}>
                <UserRound className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-sky-600 hover:bg-sky-50"
              onClick={() => {
                setEditingId(row.original.id);
                setEditFirstName(row.original.first_name || '');
                setEditLastName(row.original.last_name || '');
                setEditNationalCode(
                  (row.original.national_code || '').replace(/\D/g, '').slice(0, 10),
                );
                setEditInsuranceCode(row.original.insurance_code || '');
                setEditBirthDate(row.original.birth_date || '');
                setEditMobile(row.original.mobile || '');
                setEditPhone(row.original.phone || '');
                const nextStatus = row.original.status;
                if (
                  nextStatus === 'active' ||
                  nextStatus === 'vacation' ||
                  nextStatus === 'inactive' ||
                  nextStatus === 'terminated'
                ) {
                  setEditStatus(nextStatus);
                } else {
                  setEditStatus('active');
                }
                setEditOpen(true);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-indigo-600 hover:bg-indigo-50"
              title="حساب‌های بانکی"
              onClick={() => {
                void openBanksModal(row.original);
              }}
            >
              <CreditCard className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="مدیریت کارمندان"
        description="ثبت و ویرایش کارمندان داخل خود ماژول حسابداری."
        actions={(
          <Button
            type="button"
            className="gap-2"
            onClick={() => {
              resetAddForm();
              setAddOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            افزودن کارمند
          </Button>
        )}
      />

      <LoadingLock loading={loading}>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">لیست کارمندان</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-12">
            <div className="md:col-span-5">
              <label className="mb-1 block text-xs text-slate-600">جستجو</label>
              <div className="relative">
                <Search className="pointer-events-none absolute right-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-8"
                  placeholder="نام، کد پرسنلی، کد ملی یا موبایل"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="mb-1 block text-xs text-slate-600">فیلتر وضعیت</label>
              <select
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">همه</option>
                <option value="active">فعال</option>
                <option value="vacation">مرخصی</option>
                <option value="inactive">غیرفعال</option>
                <option value="terminated">خاتمه</option>
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="mb-1 block text-xs text-slate-600">فیلتر کارت</label>
              <select
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
                value={cardFilter}
                onChange={(e) => setCardFilter(e.target.value)}
              >
                <option value="">همه</option>
                <option value="inactive">کارت‌های غیرفعال</option>
              </select>
            </div>
            <div className="md:col-span-1 flex items-end">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setCardFilter('');
                }}
              >
                پاک
              </Button>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={filteredRows}
            emptyMessage={loading ? 'در حال بارگذاری...' : 'کارمندی ثبت نشده است.'}
          />
        </CardContent>
      </Card>
      </LoadingLock>

      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          setAddOpen(open);
          if (!open) resetAddForm();
        }}
      >
        <DialogContent className="max-w-4xl [&>button]:left-4 [&>button]:right-auto">
          <DialogHeader>
            <DialogTitle>افزودن کارمند</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-12">
            {error && (
              <div className="md:col-span-12 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </div>
            )}
            <div className="md:col-span-3">
              <label className="mb-1 block text-xs text-slate-600">نام</label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="md:col-span-3">
              <label className="mb-1 block text-xs text-slate-600">نام خانوادگی</label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs text-slate-600">کد ملی (اختیاری)</label>
              <Input dir="ltr" value={nationalCode} onChange={(e) => setNationalCode(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="۱۰ رقم؛ یکتا" />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs text-slate-600">کد بیمه</label>
              <Input value={insuranceCode} onChange={(e) => setInsuranceCode(e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs text-slate-600">تاریخ تولد</label>
              <PersianDatePickerFa value={birthDate} onChange={setBirthDate} />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs text-slate-600">موبایل</label>
              <Input dir="ltr" value={mobile} onChange={(e) => setMobile(e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs text-slate-600">تلفن</label>
              <Input dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs text-slate-600">وضعیت</label>
              <select className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value as EmployeePayload['status'])}>
                <option value="active">فعال</option>
                <option value="vacation">مرخصی</option>
                <option value="inactive">غیرفعال</option>
                <option value="terminated">خاتمه</option>
              </select>
            </div>
            <div className="md:col-span-12 flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                بستن
              </Button>
              <Button type="button" variant="secondary" disabled={submitting} onClick={() => void submitAdd(true)}>
                ثبت و مدیریت حساب بانکی
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'در حال ثبت...' : 'ثبت'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
          if (!open) setEditingId(null);
        }}
      >
        <DialogContent className="[&>button]:left-4 [&>button]:right-auto">
          <DialogHeader>
            <DialogTitle>ویرایش کارمند</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEdit} className="grid grid-cols-1 gap-3">
            <div>
              <label className="mb-1 block text-xs text-slate-600">نام</label>
              <Input
                value={editFirstName}
                onChange={(e) => setEditFirstName(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">نام خانوادگی</label>
              <Input
                value={editLastName}
                onChange={(e) => setEditLastName(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">کد ملی (اختیاری)</label>
              <Input
                dir="ltr"
                value={editNationalCode}
                onChange={(e) =>
                  setEditNationalCode(e.target.value.replace(/\D/g, '').slice(0, 10))
                }
                placeholder="۱۰ رقم؛ یکتا"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">کد بیمه</label>
              <Input value={editInsuranceCode} onChange={(e) => setEditInsuranceCode(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">تاریخ تولد</label>
              <PersianDatePickerFa value={editBirthDate} onChange={setEditBirthDate} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">موبایل</label>
              <Input
                dir="ltr"
                value={editMobile}
                onChange={(e) => setEditMobile(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">تلفن</label>
              <Input dir="ltr" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-600">وضعیت</label>
              <select
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value as EmployeePayload['status'])}
              >
                <option value="active">فعال</option>
                <option value="vacation">مرخصی</option>
                <option value="inactive">غیرفعال</option>
                <option value="terminated">خاتمه</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditOpen(false);
                  setEditingId(null);
                }}
              >
                انصراف
              </Button>
              <Button type="submit" disabled={editSubmitting}>
                {editSubmitting ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={banksOpen}
        onOpenChange={(open) => {
          if (!open && (bankFormOpen || openingBankFormRef.current)) {
            setBanksOpen(false);
            return;
          }
          setBanksOpen(open);
          if (!open) {
            setSelectedEmployee(null);
            setBankRows([]);
            setBankFormOpen(false);
            resetBankForm();
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto [&>button]:left-4 [&>button]:right-auto">
          <DialogHeader>
            <DialogTitle>
              مدیریت حساب بانکی کارمند
              {selectedEmployee
                ? `: ${selectedEmployee.full_name || `${selectedEmployee.first_name} ${selectedEmployee.last_name}`.trim()}`
                : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="mb-4 flex justify-end">
            <Button type="button" className="gap-2" onClick={() => openBankAccountForm()}>
              <Plus className="h-4 w-4" />
              افزودن حساب بانکی
            </Button>
          </div>

          <div className="mt-4 rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-700">
                  <th className="p-2 text-right">بانک</th>
                  <th className="p-2 text-right">صاحب حساب</th>
                  <th className="p-2 text-right">شماره حساب</th>
                  <th className="p-2 text-right">کارت</th>
                  <th className="p-2 text-right">شبا</th>
                  <th className="p-2 text-right">وضعیت</th>
                  <th className="p-2 text-right">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {bankRows.length === 0 ? (
                  <tr>
                    <td className="p-3 text-center text-slate-500" colSpan={7}>
                      حساب بانکی ثبت نشده است.
                    </td>
                  </tr>
                ) : (
                  bankRows.map((acc) => (
                    <tr key={acc.id} className="border-t">
                      <td className="p-2">{acc.bank?.name || '-'}</td>
                      <td className="p-2">{acc.account_holder_name || '-'}</td>
                      <td className="p-2">{acc.account_number || '-'}</td>
                      <td className="p-2">{acc.card_number || '-'}</td>
                      <td className="p-2">{acc.iban || '-'}</td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          <button
                            type="button"
                            className={`cursor-pointer rounded px-2 py-0.5 text-xs transition hover:opacity-80 ${
                              acc.is_active
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                            title={acc.is_active ? 'کلیک برای غیرفعال کردن' : 'کلیک برای فعال کردن'}
                            onClick={() => {
                              void toggleBankAccountActive(acc);
                            }}
                          >
                            {acc.is_active ? 'فعال' : 'غیرفعال'}
                          </button>
                          {acc.is_default ? (
                            <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs text-indigo-700">پیش‌فرض</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="p-2">
                        <div dir="ltr" className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-rose-600"
                            onClick={() => {
                              if (!selectedEmployee) return;
                              const usage = employeeBankAccountUsageCount(selectedEmployee.id, acc.id);
                              if (usage > 0) {
                                setError(
                                  'این حساب در اسناد/واریزی استفاده شده و قابل حذف نیست.',
                                );
                                return;
                              }
                              setBankDeleteTarget(acc);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-sky-600"
                            onClick={() => openBankAccountForm(acc)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className={`h-7 w-7 ${
                              acc.is_default
                                ? 'cursor-not-allowed bg-amber-100 text-amber-700'
                                : 'text-indigo-600 hover:bg-indigo-50'
                            }`}
                            onClick={async () => {
                              if (!selectedEmployee) return;
                              if (acc.is_default) return;
                              try {
                                await accountingApi.setDefaultEmployeeBankAccount(selectedEmployee.id, acc.id);
                                setMessage('حساب پیش‌فرض تغییر کرد.');
                                await loadEmployeeBankAccounts(selectedEmployee.id);
                              } catch (err) {
                                setError(err instanceof Error ? err.message : 'تنظیم پیش‌فرض ناموفق بود.');
                              }
                            }}
                            disabled={acc.is_default}
                            title={acc.is_default ? 'این حساب از قبل پیش‌فرض است' : 'تنظیم به‌عنوان پیش‌فرض'}
                          >
                            <Star className={`h-4 w-4 ${acc.is_default ? 'fill-current' : ''}`} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <BankAccountModal
        show={bankFormOpen && !!selectedEmployee}
        onClose={() => closeBankAccountForm(true)}
        employeeId={selectedEmployee?.id ?? 0}
        editAccount={bankEditAccount}
        banks={bankOptions}
        onSuccess={() => {
          const wasEdit = !!bankEditAccount;
          const employeeId = selectedEmployee?.id;
          closeBankAccountForm(true);
          setMessage(wasEdit ? 'حساب بانکی کارمند ویرایش شد.' : 'حساب بانکی کارمند ثبت شد.');
          if (employeeId) {
            void loadEmployeeBankAccounts(employeeId);
          }
          load();
        }}
        onError={(msg) => setError(msg)}
      />

      <ConfirmDialog
        open={Boolean(bankDeleteTarget)}
        onOpenChange={(open) => {
          if (!open) setBankDeleteTarget(null);
        }}
        title="حذف حساب بانکی"
        description="این حساب بانکی حذف شود؟"
        confirmLabel="حذف"
        cancelLabel="انصراف"
        variant="destructive"
        onConfirm={async () => {
          if (!selectedEmployee || !bankDeleteTarget) return;
          try {
            await accountingApi.deleteEmployeeBankAccount(selectedEmployee.id, bankDeleteTarget.id);
            setBankDeleteTarget(null);
            setMessage('حساب بانکی حذف شد.');
            await loadEmployeeBankAccounts(selectedEmployee.id);
            load();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'حذف حساب بانکی ناموفق بود.');
          }
        }}
      />
    </div>
  );
};

export default EmployeesPage;
